import React, { useState } from 'react';
import { Plus, ScanLine, QrCode } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { useAppSelector } from '../store/hooks';
import OverviewTab from '../components/CommunityRegistry/OverviewTab';
import HouseholdListTab from '../components/CommunityRegistry/HouseholdListTab';
import CHWPerformanceTab from '../components/CommunityRegistry/CHWPerformanceTab';
import AppSessionsTab from '../components/CommunityRegistry/AppSessionsTab';
import AddHouseholdModal from '../components/CommunityRegistry/AddHouseholdModal';
import ScanQRModal from '../components/CommunityRegistry/ScanQRModal';
import GenerateQRCodesModal from '../components/CommunityRegistry/GenerateQRCodesModal';

type TabKey = 'overview' | 'households' | 'chw-performance' | 'app-sessions';

const CommunityRegistryPage: React.FC = () => {
  const { user } = useAppSelector((state) => state.auth);
  const isSuperAdmin = user?.role === 'super_admin';
  const [tab, setTab] = useState<TabKey>('overview');
  const [addOpen, setAddOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [generateOpen, setGenerateOpen] = useState(false);
  const [scannedCode, setScannedCode] = useState<string | undefined>(undefined);
  const [refreshKey, setRefreshKey] = useState(0);

  const handleCreated = () => {
    setRefreshKey((k) => k + 1);
    setTab('households');
  };

  const handleValidScan = (code: string) => {
    setScannedCode(code);
    setScanOpen(false);
    setAddOpen(true);
  };

  const handleAddClose = () => {
    setAddOpen(false);
    setScannedCode(undefined);
  };

  return (
    <div className="@container/main space-y-4 px-4 py-6 lg:px-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Community Registry</h1>
          <p className="text-sm text-muted-foreground">
            DTU Surveillance — household registry, line-listing, and CHW performance.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={() => setAddOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Add Household
          </Button>
          <Button variant="outline" onClick={() => setScanOpen(true)}>
            <ScanLine className="mr-2 h-4 w-4" />
            Scan QR
          </Button>
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="households">Household Line-Listing</TabsTrigger>
            <TabsTrigger value="chw-performance">CHW Performance</TabsTrigger>
            {isSuperAdmin && <TabsTrigger value="app-sessions">Sessions</TabsTrigger>}
          </TabsList>
          <Button variant="outline" size="sm" onClick={() => setGenerateOpen(true)}>
            <QrCode className="mr-2 h-4 w-4" />
            Generate QR Codes
          </Button>
        </div>

        <TabsContent value="overview" className="mt-4">
          <OverviewTab refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="households" className="mt-4">
          <HouseholdListTab refreshKey={refreshKey} />
        </TabsContent>
        <TabsContent value="chw-performance" className="mt-4">
          <CHWPerformanceTab />
        </TabsContent>
        {isSuperAdmin && (
          <TabsContent value="app-sessions" className="mt-4">
            <AppSessionsTab />
          </TabsContent>
        )}
      </Tabs>

      <AddHouseholdModal
        open={addOpen}
        onClose={handleAddClose}
        onCreated={handleCreated}
        initialRegistryCode={scannedCode}
      />
      <ScanQRModal open={scanOpen} onClose={() => setScanOpen(false)} onValidCode={handleValidScan} />
      <GenerateQRCodesModal open={generateOpen} onClose={() => setGenerateOpen(false)} />
    </div>
  );
};

export default CommunityRegistryPage;
